import { useEffect, memo, useCallback, useRef } from 'react'
import { useStartBrandPlan } from 'hooks/queries/brandPlans/useStartBrandPlan'
import { useOnboard } from 'hooks/queries/useOnboard'
import dayjs from 'dayjs'
import { useCampaignGenerationLoadingContext } from './CampaignGenerationLoadingContext'
import { useSession } from 'hooks/useSession'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { useSchedules } from 'hooks/queries/workspaces/schedules/useSchedules'
import { usePollUntilPendingBrandPlanSchedules } from 'hooks/queries/schedules/usePollUntilPendingBrandPlanSchedules'
import { FinalStep } from 'screens/VirtualMarketerOnboarding/FinalStep/FinalStep'
import { useUpdateUserAppPreferences } from 'hooks/queries/useUpdateUserAppPreferences'
import { usePollBrandProfileAnalysis } from 'components/FirstTimeExperienceModal/steps/BrandProfile/usePollBrandProfileAnalysis'
import { AIJobStatus } from 'types'

export interface CampaignGenerationLoadingScreenProps {
  brandPlanId: number
  brandProfileId?: number
  analysisId?: number
  campaignData?: {
    growth_goals_input: string
    key_dates_input: string
  }
  startDate?: string
  subscriptionPriceId?: string
  onComplete: () => void
  onBack: () => void
  className?: string
}

export const CampaignGenerationLoadingScreen = memo(
  ({
    brandPlanId,
    brandProfileId,
    analysisId,
    campaignData,
    startDate,
    subscriptionPriceId,
    onComplete,
    onBack,
    className,
  }: CampaignGenerationLoadingScreenProps) => {
    const { mutateAsync: updatePreferences } = useUpdateUserAppPreferences()
    const { logout } = useSession()
    const schedulesQuery = useSchedules({
      orderBy: 'start_date',
      orderDirection: 'asc',
      items: 15,
      startDate: dayjs().format('YYYY-MM-DD'),
    })
    const { setProgress, loadingPercentage, isComplete } = useCampaignGenerationLoadingContext()
    const { mutateAsync: startBrandPlan } = useStartBrandPlan({ brandPlanId })
    const { mutateAsync: onboard } = useOnboard()
    const pollUntilPendingSchedules = usePollUntilPendingBrandPlanSchedules({})
    const hasStartedGeneratingRef = useRef(false)

    const shouldPollForProfile = brandProfileId != null && analysisId != null
    const { data: analysisData } = usePollBrandProfileAnalysis(
      { id: analysisId ?? 0, brandProfileId: brandProfileId ?? 0 },
      { enabled: shouldPollForProfile },
    )

    const handleLogout = useCallback(async () => {
      await logout()
    }, [logout])

    const analysisStatus = analysisData?.brand_profile_analysis?.status
    const isAnalysisComplete = analysisStatus === AIJobStatus.Successful
    const isAnalysisFailed = analysisStatus === AIJobStatus.Failed || analysisStatus === AIJobStatus.Expired
    const canStartBrandPlan = !shouldPollForProfile || isAnalysisComplete

    useEffect(() => {
      if (shouldPollForProfile) {
        setProgress(10)
      }
    }, [shouldPollForProfile, setProgress])

    useEffect(() => {
      if (shouldPollForProfile && isAnalysisFailed) {
        showErrorMessageToast('Brand profile analysis failed. Please try again.')
        onBack()
      }
    }, [shouldPollForProfile, isAnalysisFailed, onBack])

    useEffect(() => {
      if (!canStartBrandPlan || hasStartedGeneratingRef.current) return
      hasStartedGeneratingRef.current = true

      const abortController = new AbortController()

      const startGenerating = async () => {
        try {
          if (!shouldPollForProfile) {
            setProgress(10)
          }

          await startBrandPlan({
            growth_goals_input: campaignData?.growth_goals_input,
            key_dates_input: campaignData?.key_dates_input,
            start_date: startDate,
            subscription_price_id: subscriptionPriceId,
            signal: abortController.signal,
          })

          await schedulesQuery.refetch()
          await pollUntilPendingSchedules()

          setProgress(80)

          await onboard()
          await updatePreferences({ scheduledPostsView: 'calendar' })

          setProgress(100)

          onComplete()
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') {
            return
          }
          showErrorMessageToast('Failed to generate campaigns. Please try again.')
          onBack()
        }
      }

      startGenerating()

      return () => {
        abortController.abort()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canStartBrandPlan])

    return <FinalStep finishedLoading={isComplete} />
  },
)

CampaignGenerationLoadingScreen.displayName = 'CampaignGenerationLoadingScreen'
